/**
 * Admin-only Excel export for a single order's prescription.
 *
 * R36 rewrite of the manufacturing-friendly .xlsx export. Three goals:
 *   1. Match the wizard's canonical field order per section, with one
 *      empty separator row BETWEEN sections (never inside one).
 *   2. Render only meaningful data: skip null/empty values, hide
 *      mutually-exclusive material fields that don't apply, hide L/R
 *      rows when that side is 'none', hide "Other" text fields unless
 *      the parent picked 'other'.
 *   3. Two soft pastel fills mark Left-foot vs Right-foot rows so the
 *      lab can scan a long sheet at a glance, plus a light-gray fill
 *      for each section header row.
 *
 * Styling note: SheetJS open-source ('xlsx') does NOT support cell
 * styles, so this file uses 'xlsx-js-style' (community fork that keeps
 * the SheetJS API shape and adds an `s` style key on cells). Only this
 * file imports it; the rest of the codebase still uses 'xlsx'.
 *
 * The xlsx-js-style library is dynamically imported on click so admins
 * who never click Export pay no bundle cost.
 */
import type { OrderDetail } from '@insolech/sdk';
import type {
  PrescriptionDeepState,
  InsoleDesignState,
} from './wizard/prescription/prescriptionState';
import { isCustomLine, resolvePatientDisplay } from './productLabel';
import { isSandalLine } from './isSandalLine';
import {
  isRearfootPostType,
  normalizeRearfootWedgeType,
} from './wizard/prescription/rearfootWedgeRules';
import {
  KID_COLOR_LABEL,
  formatHardness,
  formatExtensionSoftness,
  formatMcoLocation,
  formatMcoSoftness,
} from './wizard/prescription/summary';
import { ORTHOTIC_INTENDED_USE_OPTIONS } from './wizard/prescription/orthoticIntendedUse';
import { formatEvaThicknessMm } from './wizard/prescription/evaThicknessRules';

/** Side tag drives the row fill color when the workbook is written. */
type Side = 'L' | 'R' | null;
/** `emphasis` rows get the loud amber/bold treatment (multi-pair quantity).
 *  `smallFont` rows render at 8pt (patient notes — a long free-text block kept
 *  compact so it doesn't dominate the sheet). */
type Row = { label: string; value: string | number; side: Side; emphasis?: boolean; smallFont?: boolean };

/** A section is a header + either a flat list of rows OR a list of
 *  "card groups". For sections without L/R groupings (PATIENT, ORDER,
 *  LINE, INSOLE DESIGN, OBSERVATIONS, etc.) use `rows`. For modification
 *  sections (REARFOOT/MIDFOOT/FOREFOOT/OFFLOADINGS) use `cards`: each
 *  card is one modification's rows, already ordered L-first-then-R, and
 *  the writer inserts a blank separator row between cards. Cards with
 *  zero rows are skipped (no trailing blank row leaks through).
 *  Empty top-level sections are dropped entirely so the sheet stays
 *  compact. */
interface Section {
  title: string;
  rows?: Row[];
  cards?: Row[][];
}

/* ---------- Value helpers --------------------------------------------- */

const num = (v: number | null | undefined, suffix = ''): string => {
  if (v == null || !Number.isFinite(v)) return '';
  return `${v}${suffix}`;
};

const yesNo = (v: boolean | undefined): string =>
  v === true ? 'Yes' : v === false ? 'No' : '';

const str = (v: unknown): string => (v == null || v === '' ? '' : String(v));

/** Title-case a snake-case enum value: "heel_offloading" -> "Heel offloading". */
const titleCase = (v: string | undefined | null): string => {
  if (!v) return '';
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Canonical label lookups reused from the form so the Excel never drifts from
 *  what the podiatrist saw. Fall back to titleCase for unknown/legacy values. */
const ORTHOTIC_USE_LABEL: Record<string, string> = Object.fromEntries(
  ORTHOTIC_INTENDED_USE_OPTIONS.map((o) => [o.value, o.label]),
);
/** First-ray / MTPJ cut-out enum → label (preserves the MTPJ acronym that
 *  titleCase would mangle to "Mtpj"). */
const CUT_OUT_LABEL: Record<string, string> = {
  '1st_ray': '1st Ray cut-out',
  '1st_mtpj': '1st MTPJ cut-out',
};
/** Rearfoot wedge type → the form's exact SELECT label (post-normalization).
 *  Legacy 'intrinsic'/'extrinsic' are normalized first, so an old 'extrinsic'
 *  reads as the EVA post rather than the ambiguous "Extrinsic". */
const REARFOOT_WEDGE_TYPE_LABEL: Record<string, string> = {
  kirby_skive: 'Kirby Skive (intrinsic)',
  rearfoot_post_intrinsic: 'Rearfoot post (intrinsic)',
  rearfoot_eva_post_extrinsic: 'Rearfoot EVA post (extrinsic)',
};
const rearfootWedgeTypeLabel = (t: string | undefined): string => {
  const n = normalizeRearfootWedgeType(t as never) ?? undefined;
  return (n && REARFOOT_WEDGE_TYPE_LABEL[n]) || titleCase(t);
};
/** Top-cover material: collapse the legacy "Poron 3mm" to "Poron" (matches
 *  summarizeTopCover). */
const normalizeTopCoverMaterial = (v: unknown): string =>
  v === 'Poron 3mm' ? 'Poron' : str(v);

/** Drop-met-pad / MTT-bar span location enum → "2 to 3" (matches summary). */
const SPAN_LOCATION_LABEL: Record<string, string> = {
  m2_3: '2 to 3',
  m3_4: '3 to 4',
  m2_4: '2 to 4',
  m1_5: '1 to 5',
  m2_5: '2 to 5',
};
const spanLocationLabel = (v: string | undefined): string =>
  (v && SPAN_LOCATION_LABEL[v]) || titleCase(v);
/** Fascial accommodation option → label (matches summary "Yes + Poron"). */
const fascialLabel = (v: string | undefined): string =>
  v === 'yes_poron' ? 'Yes + Poron' : v === 'yes' ? 'Yes' : titleCase(v);
/** MLA extra filled option → label (matches summary "3mm Poron"; the enum's
 *  "12deg" tail is a fixed spec the app never surfaces, so it is dropped). */
const mlaExtraFilledLabel = (v: string | undefined): string =>
  v === 'poron_3mm_12deg' ? '3mm Poron' : titleCase(v);
/** Thin wrappers so summary's string-typed formatters accept optional values
 *  and return '' (which the empty-row filter drops) instead of throwing. */
const hardnessLabel = (v: string | undefined): string => (v ? formatHardness(v) : '');
const extSoftnessLabel = (v: string | undefined): string => formatExtensionSoftness(v) ?? '';
const mcoLocationLabel = (v: string | undefined): string => (v ? formatMcoLocation(v) : '');
const mcoSoftnessLabel = (v: string | undefined): string => (v ? formatMcoSoftness(v) : '');

/** Build a row, tagging it as L/R/none so the writer can paint it later. */
const row = (label: string, value: string | number | null | undefined, side: Side = null): Row => ({
  label,
  value: value == null || value === '' ? '' : (typeof value === 'number' ? value : String(value)),
  side,
});

/** Drop rows whose value is empty. Header / "always shown" rows pass null
 *  here as the value of `keep` to opt out of the filter. */
const isMeaningful = (r: Row): boolean => {
  if (typeof r.value === 'number') return Number.isFinite(r.value);
  return r.value !== '' && r.value !== null && r.value !== undefined;
};

/** Conditionally push a row only when its value is meaningful. */
const push = (
  rows: Row[],
  label: string,
  value: string | number | null | undefined,
  side: Side = null,
): void => {
  const r = row(label, value, side);
  if (isMeaningful(r)) rows.push(r);
};

/** Card builder — accumulates Left rows and Right rows separately so
 *  they can be flushed L-first-then-R inside a single card group.
 *  Use `addPair(label, left, right, lActive, rActive)` for each field of
 *  the card, then call `.toCard()` to get the merged Row[] (L rows then
 *  R rows). Returns `null` if both sides are empty. */
type CardBuilder = {
  addPair: (
    label: string,
    left: string | number | null | undefined,
    right: string | number | null | undefined,
    leftActive: boolean,
    rightActive: boolean,
  ) => void;
  toCard: () => Row[] | null;
};

const makeCard = (): CardBuilder => {
  const lefts: Row[] = [];
  const rights: Row[] = [];
  return {
    addPair: (label, left, right, leftActive, rightActive) => {
      if (leftActive) {
        const r = row(`${label} — Left foot`, left, 'L');
        if (isMeaningful(r)) lefts.push(r);
      }
      if (rightActive) {
        const r = row(`${label} — Right foot`, right, 'R');
        if (isMeaningful(r)) rights.push(r);
      }
    },
    toCard: () => {
      if (lefts.length === 0 && rights.length === 0) return null;
      return [...lefts, ...rights];
    },
  };
};

/* ---------- Date formatting (unchanged from R35) ---------------------- */

const formatDate = (iso: string | null | undefined): string =>
  iso ? iso.slice(0, 10) : '';

/** Whole-years age from a DOB date string as of a reference ISO timestamp.
 *  UTC calendar arithmetic (DOB is a plain date, parsed as UTC midnight). */
const ageFromDob = (dob: string | null | undefined, asOfIso: string): string => {
  if (!dob) return '';
  const born = new Date(dob);
  const asOf = new Date(asOfIso);
  if (Number.isNaN(born.getTime()) || Number.isNaN(asOf.getTime())) return '';
  let age = asOf.getUTCFullYear() - born.getUTCFullYear();
  const monthDiff = asOf.getUTCMonth() - born.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getUTCDate() < born.getUTCDate())) age--;
  return age >= 0 && age <= 130 ? String(age) : '';
};

/* ---------- Section builders ------------------------------------------ */

function buildOrderSection(order: OrderDetail): Section {
  const rows: Row[] = [];
  push(rows, 'Order number', order.order_number);
  // Multi-quantity banner at the very top of the sheet: providers must never
  // miss a 2+ units order, and the LINE section's own quantity row sits far
  // down a long sheet. Omitted entirely for the common single-unit case.
  // "PAIRS" wording only when every multi-quantity line is a custom
  // orthotic; marketplace/equipment quantities are individual items.
  const multiQtyLines = order.lines.filter((l) => (l.quantity ?? 1) > 1);
  if (multiQtyLines.length > 0) {
    const total = order.lines.reduce((sum, l) => sum + (l.quantity ?? 1), 0);
    const allCustom = multiQtyLines.every((l) => isCustomLine(l.line_type));
    const unit = allCustom ? 'PAIRS' : 'ITEMS';
    rows.push({
      label: allCustom ? 'PAIRS REQUIRED' : 'QUANTITY REQUIRED',
      value:
        order.lines.length === 1
          ? `${total} ${unit}`
          : `${total} ${unit} TOTAL (see line sections)`,
      side: null,
      emphasis: true,
    });
  }
  push(rows, 'Status', titleCase(order.status));
  push(rows, 'Created at', formatDate(order.created_at));
  push(rows, 'Submitted at', formatDate(order.submitted_at));
  push(rows, 'Expected delivery', order.expected_delivery_date ?? '');
  push(rows, 'Clinic', order.clinic?.name ?? '');
  push(rows, 'Podiatrist', order.podiatrist?.full_name ?? '');
  push(rows, 'Notes', str(order.notes));
  push(rows, 'Admin observations', str(order.admin_observations));
  return { title: 'ORDER', rows };
}

/** PATIENT section — exact field order per task spec:
 *   patient name, age, gender, weight, foot size, orthotic intended use,
 *   shoe brand, phone, email, patient notes
 *
 * Age and patient notes aren't currently selected by the OrderDetail
 * query, so they're sourced opportunistically from the first line's
 * configuration JSONB (which is where the wizard could persist them in
 * future). When still unavailable, the empty-row filter drops them so
 * no blank rows leak through.
 */
function buildPatientSection(order: OrderDetail): Section {
  const rows: Row[] = [];

  // Pull patient-level metrics from the first line that has them. The
  // wizard writes weight/foot size/etc. onto every line's configuration,
  // so any line works. Marketplace lines (orthopaedic, fito, etc.) skip
  // these fields, so we scan until we find a populated one.
  const findCfg = (key: string): unknown => {
    for (const line of order.lines) {
      const cfg = (line.configuration ?? {}) as Record<string, unknown>;
      if (cfg[key] != null && cfg[key] !== '') return cfg[key];
    }
    return undefined;
  };

  const weightKg = findCfg('weight_kg');
  const leftFoot = findCfg('left_foot_size') as { value: string | number; unit: string } | undefined;
  const rightFoot = findCfg('right_foot_size') as { value: string | number; unit: string } | undefined;
  const orthoticUse = findCfg('orthotic_intended_use') as string | undefined;
  const shoeBrand = findCfg('shoe_brand') as string | undefined;
  // Age is DERIVED, never stored: patients carries only date_of_birth
  // (nullable). Computed as of the order's submission (created_at fallback)
  // so a sheet regenerated years later still prints the age the patient had
  // when this order was prescribed. No DOB, an unparseable DOB, or an
  // implausible result (DOB after the order, >130y) yields '' and the
  // empty-row filter drops the Age row entirely rather than printing a wrong
  // number. Record-less/legacy orders keep the config-key fallback.
  const dobAge = ageFromDob(
    order.patient?.date_of_birth,
    order.submitted_at ?? order.created_at,
  );
  const patientAge = dobAge !== '' ? dobAge : (findCfg('patient_age') ?? findCfg('age'));
  // Record-less reorders persist gender on the line config (no patients row).
  const cfgGender = findCfg('gender') as string | undefined;
  // Patient notes come from the patient record (patients.notes) — the single
  // source of truth, so an edit to the patient's note flows into any future
  // regenerated Excel. Legacy record-less orders (patient_id NULL) have no
  // patient row, so this is null and the empty-row filter drops it. The config
  // keys are a forward-compatible fallback and are NOT the podiatrist's
  // "Notes for the lab" (`configuration.observations`), which is deliberately
  // excluded here and rendered in the PODIATRIST OBSERVATIONS section instead.
  const patientNotes =
    order.patient?.notes ?? findCfg('patient_notes') ?? findCfg('patient_observations');

  // Legacy orders (patient_id NULL) carry the name as free-text on the line;
  // resolve via the shared resolver so the supplier Excel is never blank and
  // reflects an in-app name correction to either store (patients record or
  // order_lines.patient_name).
  push(rows, 'Patient name', resolvePatientDisplay(order).name ?? '');
  push(rows, 'Age', patientAge != null ? String(patientAge) : '');
  push(rows, 'Gender', titleCase(order.patient?.gender ?? cfgGender ?? ''));
  push(rows, 'Weight (kg)', weightKg != null ? String(weightKg) : '');
  if (leftFoot) push(rows, 'Left foot size', `${leftFoot.value} ${leftFoot.unit}`);
  if (rightFoot) push(rows, 'Right foot size', `${rightFoot.value} ${rightFoot.unit}`);
  // Orthotic intended use. The row is FORCED on any order with an insole
  // line so the lab always finds it. The wizard's select now starts empty
  // and an insole cannot be submitted until a use is chosen, so new lines
  // store an active choice and print its form label. Lines written while
  // the select still preselected 'everyday_comfort' store that value too;
  // it prints as itself because a chosen value and an inherited default
  // cannot be told apart. Only an ABSENT or BLANK key prints
  // "Not specified", the same rule as the 'EVA thickness (mm)' row, because
  // the sheet must not invent a choice that was never on screen.
  // Not on a sandal-only order: the sandal flow no longer asks for it, so
  // the row prints only when a value was actually stored (older sandal lines
  // hold the auto-filled default) and the sheet never invents one.
  const allSandal = order.lines.length > 0 && order.lines.every((l) => isSandalLine(l));
  const orthoticUseDisplay = orthoticUse
    ? (ORTHOTIC_USE_LABEL[orthoticUse] ?? titleCase(orthoticUse))
    : '';
  if (allSandal) {
    push(rows, 'Orthotic intended use', orthoticUseDisplay);
  } else {
    rows.push(row('Orthotic intended use', orthoticUseDisplay || 'Not specified'));
  }
  push(rows, 'Shoe brand', str(shoeBrand));
  push(rows, 'Phone', str(order.patient?.phone));
  push(rows, 'Email', str(order.patient?.email));
  // Patient notes render at 8pt (see Row.smallFont) so a long free-text block
  // stays compact and doesn't dominate the sheet.
  const notesRow = row('Patient notes', patientNotes != null ? String(patientNotes) : '');
  if (isMeaningful(notesRow)) {
    notesRow.smallFont = true;
    rows.push(notesRow);
  }

  return { title: 'PATIENT', rows };
}

/** LINE section — line metadata only. Patient-level metrics live in the
 *  PATIENT section. Per task: drop unit_price and patient_name from
 *  the line (the former is admin pricing, the latter duplicates the
 *  PATIENT section). */
function buildLineSection(line: OrderDetail['lines'][number]): Section {
  const rows: Row[] = [];
  // Product + SKU rows intentionally removed (Rafael): products.name_en /
  // products.sku are the linked CATALOG product, which is wrong for custom
  // lines (e.g. a carbon-fibre insole showed "Custom EVA insole" /
  // "CUST-EVA-STD"). The Solution subtype below is the authoritative label.
  push(rows, 'Solution subtype', titleCase(line.solution_subtype));
  // Single unit keeps the discreet historical row; 2+ gets the emphasized
  // treatment so the count cannot be missed mid-sheet either. "PAIRS"
  // wording is reserved for custom orthotic lines; marketplace/equipment
  // quantities are individual items.
  if ((line.quantity ?? 1) > 1) {
    const custom = isCustomLine(line.line_type);
    rows.push({
      label: custom ? 'Quantity (pairs)' : 'Quantity',
      value: custom ? `${line.quantity} PAIRS` : `${line.quantity} ITEMS`,
      side: null,
      emphasis: true,
    });
  } else {
    push(rows, 'Quantity', line.quantity);
  }
  return { title: `LINE ${line.line_number}`, rows };
}

/** INSOLE DESIGN — material panel is mutually exclusive. The subtype
 *  field on the line (e.g. 'pa12_insole') determines which material
 *  family was picked and therefore which thickness/color rows belong on
 *  the sheet. Anything outside the active family is dropped, even if
 *  stale values linger in the JSONB. */
function buildInsoleDesignSection(
  d: InsoleDesignState | undefined,
  subtype: string | null | undefined,
  orderNumber: string | null | undefined,
): Section | null {
  if (!d) return null;
  const rows: Row[] = [];

  // ---- Material-specific thickness / colour --------------------------
  // Mirrors the MaterialPanel branch logic in InsoleDesignSection.tsx.
  const isTextBox =
    subtype === 'pa12_insole' || subtype === 'tpu_insole' || subtype === 'polypropylene_insole';

  if (isTextBox) {
    push(rows, 'Shell thickness (mm)', d.shell_thickness_mm != null ? num(d.shell_thickness_mm, ' mm') : '');
    // (No polypropylene colour row: `polypropylene_color` is declared in state
    // but no form control ever writes it, so it is always empty. Removed.)
  } else if (subtype === 'carbon_insole') {
    push(rows, 'Shell thickness', str(d.shell_thickness));
  } else if (subtype === 'fibreglass_insole') {
    push(rows, 'Fibreglass color', titleCase(d.fibreglass_color));
    push(rows, 'Shell thickness', str(d.shell_thickness));
  } else if (subtype === 'eva_insole') {
    push(rows, 'EVA type', str(d.eva_type));
    // EVA base thickness is FORCED onto the sheet: EVA lines written before
    // the field existed have no value, and the lab must read "Not specified"
    // for those, never the wizard's 2 mm default for new prescriptions.
    // "2 mm" / "2.5 mm" through the shared formatter (never "2.0 mm"), the
    // same text the workshop PDF prints. The shell rows above reach the same
    // "2.5 mm" shape through `num(v, ' mm')`; keep the two in step.
    rows.push(
      row(
        'EVA thickness (mm)',
        d.eva_thickness_mm != null ? `${formatEvaThicknessMm(d.eva_thickness_mm)} mm` : 'Not specified',
      ),
    );
  } else {
    // Unknown subtype (sandal / refurbishment / null): be forgiving and
    // emit whatever thickness field is populated.
    push(rows, 'Shell thickness (mm)', d.shell_thickness_mm != null ? num(d.shell_thickness_mm, ' mm') : '');
    push(rows, 'Shell thickness', str(d.shell_thickness));
    push(rows, 'Fibreglass color', titleCase(d.fibreglass_color));
    push(rows, 'EVA type', str(d.eva_type));
    push(
      rows,
      'EVA thickness (mm)',
      d.eva_thickness_mm != null ? `${formatEvaThicknessMm(d.eva_thickness_mm)} mm` : '',
    );
  }

  // ---- Geometry ------------------------------------------------------
  push(rows, 'Insole length', titleCase(d.insole_length));
  // Insole cup is hidden on refurbishment orders (the existing shell keeps its
  // cup), where the form auto-seeds a default the operator never chose. Skip it.
  const isRefurb = subtype === 'insoles_refurbishment';
  if (!isRefurb) {
    push(rows, 'Insole cup', titleCase(d.insole_cup));
    // 'Other' free-text only when the parent picked 'other'.
    if (d.insole_cup === 'other') {
      push(rows, 'Insole cup (other)', str(d.insole_cup_other));
    }
  }

  // ---- Top cover -----------------------------------------------------
  push(rows, 'Top cover — patient type', titleCase(d.top_cover_patient_type));
  push(rows, 'Top cover — material', normalizeTopCoverMaterial(d.top_cover_material));
  push(rows, 'Top cover — layer', titleCase(d.top_cover_layer));
  // Kid colour only renders when the kid patient type is active.
  if (d.top_cover_patient_type === 'kid') {
    const kid = d.top_cover_kid_color;
    push(rows, 'Top cover — kid color', kid ? (KID_COLOR_LABEL[kid] ?? titleCase(kid)) : '');
  } else {
    push(rows, 'Top cover — color', str(d.top_cover_color));
  }

  // ---- Naming / notes ------------------------------------------------
  // ENGRAVING: on PA12 + TPU custom insoles the "Custom name" is what gets
  // LASERED onto the shell. INSOLECH always prefixes it with the order number
  // for order management + traceability, so the engraving reads
  // "{order_number} - {name}" (or just "{order_number}" when no name was
  // entered). Other materials keep the plain name. EXCEL-ONLY: the wizard /
  // edit-prescription custom-name field is unchanged - the operator never sees
  // the order ID there, and the Prophet-name warning logic is untouched.
  if (subtype === 'pa12_insole' || subtype === 'tpu_insole') {
    const name = str(d.custom_name).trim();
    const on = str(orderNumber).trim();
    const engraving = name ? (on ? `${on} - ${name}` : name) : on;
    push(rows, 'Custom name', engraving);
  } else {
    push(rows, 'Custom name', str(d.custom_name));
  }
  // The form relabels "Design remarks" to "Refurbishment remarks" on refurb.
  push(rows, isRefurb ? 'Refurbishment remarks' : 'Design remarks', str(d.design_remarks));

  return { title: 'INSOLE DESIGN', rows };
}

/** Helper — a side is "active" when its type/level is set and not 'none'. */
const sideActive = (v: string | undefined): boolean => !!v && v !== 'none';
/** Boolean-shaped sides (Yes/No toggles) are active when the value is
 *  explicitly true. False/undefined drop the row. */
const sideActiveBool = (v: boolean | undefined): boolean => v === true;

function buildRearfootSection(
  rf: PrescriptionDeepState['rearfoot'] | undefined,
  isSandal: boolean,
): Section | null {
  if (!rf) return null;
  const cards: Row[][] = [];

  // 1. Subcalcaneal offloading (active per side when type is set + not 'none')
  const sub = rf.subcalcaneal_offloading;
  if (sub) {
    const lActive = sideActive(sub.left?.type);
    const rActive = sideActive(sub.right?.type);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Subcalcaneal offloading — type',
        titleCase(sub.left?.type), titleCase(sub.right?.type), lActive, rActive);
      cb.addPair('Subcalcaneal offloading — fill',
        titleCase(sub.left?.fill), titleCase(sub.right?.fill), lActive, rActive);
      cb.addPair('Subcalcaneal offloading — depth (mm)',
        num(sub.left?.depth_mm), num(sub.right?.depth_mm), lActive, rActive);
      cb.addPair('Subcalcaneal offloading — position',
        titleCase(sub.left?.position), titleCase(sub.right?.position), lActive, rActive);
      cb.addPair('Subcalcaneal offloading — dimension',
        titleCase(sub.left?.dimension), titleCase(sub.right?.dimension), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 2. Heel raise
  const hr = rf.heel_raise;
  if (hr) {
    const lActive = sideActive(hr.left_type);
    const rActive = sideActive(hr.right_type);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Heel raise — type',
        titleCase(hr.left_type), titleCase(hr.right_type), lActive, rActive);
      cb.addPair('Heel raise — length',
        titleCase(hr.left_length), titleCase(hr.right_length), lActive, rActive);
      cb.addPair('Heel raise — height (mm)',
        num(hr.left_height_mm), num(hr.right_height_mm), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 3. Rearfoot posting (boolean toggle, Yes/No)
  // Informative default: 'Rearfoot posting' defaults to Yes for both
  // sides. Always render even when the field is empty/unset so the lab
  // knows posting is included as part of the standard prescription.
  // Hidden for sandals (the sandal form has no rearfoot-posting card), so the
  // seeded Yes/Yes default never leaks onto a sandal sheet.
  const rp = rf.rearfoot_posting;
  if (!isSandal) {
    const leftEnabled = rp?.left_enabled ?? true;
    const rightEnabled = rp?.right_enabled ?? true;
    const cb = makeCard();
    cb.addPair('Rearfoot posting',
      yesNo(leftEnabled), yesNo(rightEnabled), true, true);
    const card = cb.toCard();
    if (card) cards.push(card);
  }

  // 4. Rearfoot wedge
  const rw = rf.rearfoot_wedge;
  if (rw) {
    const lActive = sideActive(rw.left_type);
    const rActive = sideActive(rw.right_type);
    if (lActive || rActive) {
      // R38: the intrinsic rearfoot post + extrinsic EVA post carry NO depth
      // (post thickness is set by the angle). Only the Kirby skive keeps a
      // depth row. The Excel is the manufacturing spec, so it must follow the
      // CURRENT form rules even when a legacy stored depth_mm lingers from
      // before R38. Normalize legacy 'intrinsic'/'extrinsic' first so an old
      // 'extrinsic' draft is correctly treated as a post type. Decided per side.
      const leftIsPost = isRearfootPostType(normalizeRearfootWedgeType(rw.left_type));
      const rightIsPost = isRearfootPostType(normalizeRearfootWedgeType(rw.right_type));
      const cb = makeCard();
      cb.addPair('Rearfoot wedge — type',
        rearfootWedgeTypeLabel(rw.left_type), rearfootWedgeTypeLabel(rw.right_type), lActive, rActive);
      cb.addPair('Rearfoot wedge — correction',
        titleCase(rw.left_correction), titleCase(rw.right_correction), lActive, rActive);
      cb.addPair('Rearfoot wedge — angle (degrees)',
        num(rw.left_degrees, '°'), num(rw.right_degrees, '°'), lActive, rActive);
      cb.addPair('Rearfoot wedge — depth (mm)',
        num(rw.left_depth_mm), num(rw.right_depth_mm),
        lActive && !leftIsPost, rActive && !rightIsPost);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // Observations moved to a dedicated PODIATRIST OBSERVATIONS section
  // at the end of the workbook (collected by buildSections).

  return cards.length ? { title: 'REARFOOT MODIFICATIONS', cards } : null;
}

function buildMidfootSection(
  mf: PrescriptionDeepState['midfoot'] | undefined,
  isSandal: boolean,
): Section | null {
  if (!mf) return null;
  const cards: Row[][] = [];

  // 1a. Arch height adjustment — MLA (own card)
  const aha = mf.arch_height_adjustment;
  if (aha) {
    const mlaL = sideActive(aha.mla_left_level);
    const mlaR = sideActive(aha.mla_right_level);
    if (mlaL || mlaR) {
      const cb = makeCard();
      cb.addPair('Arch height (MLA) — direction',
        titleCase(aha.mla_left_level), titleCase(aha.mla_right_level), mlaL, mlaR);
      cb.addPair('Arch height (MLA) — height (mm)',
        num(aha.mla_left_height_mm), num(aha.mla_right_height_mm), mlaL, mlaR);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
    // 1b. Arch height adjustment — LLA (own card)
    const llaL = sideActive(aha.lla_left_level);
    const llaR = sideActive(aha.lla_right_level);
    if (llaL || llaR) {
      const cb = makeCard();
      cb.addPair('Arch height (LLA) — direction',
        titleCase(aha.lla_left_level), titleCase(aha.lla_right_level), llaL, llaR);
      cb.addPair('Arch height (LLA) — height (mm)',
        num(aha.lla_left_height_mm), num(aha.lla_right_height_mm), llaL, llaR);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 2. Fascial accommodation
  const fa = mf.fascial_accommodation;
  if (fa) {
    const lActive = sideActive(fa.left);
    const rActive = sideActive(fa.right);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Fascial accommodation',
        fascialLabel(fa.left), fascialLabel(fa.right), lActive, rActive);
      cb.addPair('Fascial accommodation — depth (mm)',
        num(fa.left_depth_mm), num(fa.right_depth_mm), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 3. MLA extra filled
  const mef = mf.mla_extra_filled;
  if (mef) {
    const lActive = sideActive(mef.left);
    const rActive = sideActive(mef.right);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('MLA extra filled',
        mlaExtraFilledLabel(mef.left), mlaExtraFilledLabel(mef.right), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 4. Medial flange (Yes/No toggle). Hidden for sandals.
  const medial = mf.medial_flange;
  if (!isSandal && medial) {
    const lActive = sideActiveBool(medial.left_enabled);
    const rActive = sideActiveBool(medial.right_enabled);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Medial flange',
        yesNo(medial.left_enabled), yesNo(medial.right_enabled), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 5. Lateral flange (Yes/No toggle). Hidden for sandals.
  const lateral = mf.lateral_flange;
  if (!isSandal && lateral) {
    const lActive = sideActiveBool(lateral.left_enabled);
    const rActive = sideActiveBool(lateral.right_enabled);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Lateral flange',
        yesNo(lateral.left_enabled), yesNo(lateral.right_enabled), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 6. Arch reinforcement (Yes/No toggle per side, MLA + LLA as two cards).
  //    Boolean keys are the current shape; legacy enum keys are read as
  //    a fallback so old drafts still render. Hidden for sandals.
  const ar = mf.arch_reinforcement;
  if (!isSandal && ar) {
    const sideOn = (kind: 'mla' | 'lla', side: 'left' | 'right'): boolean | undefined => {
      const boolKey = `${kind}_${side}_enabled` as const;
      if (ar[boolKey] != null) return ar[boolKey] === true;
      const legacyKey = `${kind}_${side}_level` as const;
      const level = ar[legacyKey];
      if (level == null) return undefined;
      return level !== 'none';
    };
    const mlaL = sideOn('mla', 'left') === true;
    const mlaR = sideOn('mla', 'right') === true;
    if (mlaL || mlaR) {
      const cb = makeCard();
      cb.addPair('Arch reinforcement (MLA)',
        yesNo(mlaL), yesNo(mlaR), mlaL, mlaR);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
    const llaL = sideOn('lla', 'left') === true;
    const llaR = sideOn('lla', 'right') === true;
    if (llaL || llaR) {
      const cb = makeCard();
      cb.addPair('Arch reinforcement (LLA)',
        yesNo(llaL), yesNo(llaR), llaL, llaR);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // Observations moved to a dedicated PODIATRIST OBSERVATIONS section
  // at the end of the workbook (collected by buildSections).

  return cards.length ? { title: 'MIDFOOT MODIFICATIONS', cards } : null;
}

function buildForefootSection(
  ff: PrescriptionDeepState['forefoot'] | undefined,
  isSandal: boolean,
): Section | null {
  if (!ff) return null;
  const cards: Row[][] = [];

  // 1. Forefoot wedge
  const wedge = ff.forefoot_wedge;
  if (wedge) {
    const lActive = sideActive(wedge.left_type);
    const rActive = sideActive(wedge.right_type);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Forefoot wedge — type',
        titleCase(wedge.left_type), titleCase(wedge.right_type), lActive, rActive);
      cb.addPair('Forefoot wedge — correction',
        titleCase(wedge.left_correction), titleCase(wedge.right_correction), lActive, rActive);
      cb.addPair('Forefoot wedge — height (mm)',
        num(wedge.left_height_mm), num(wedge.right_height_mm), lActive, rActive);
      // Hardness only applies to the extrinsic (EVA post) wedge; the form hides
      // it for intrinsic and clears any stale value. Gate per side to match.
      cb.addPair('Forefoot wedge — hardness',
        hardnessLabel(wedge.left_hardness), hardnessLabel(wedge.right_hardness),
        lActive && wedge.left_type === 'extrinsic', rActive && wedge.right_type === 'extrinsic');
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 2. Gait plate. Hidden for sandals.
  const gp = ff.gait_plate;
  if (!isSandal && gp) {
    const lActive = sideActive(gp.left);
    const rActive = sideActive(gp.right);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Gait plate',
        titleCase(gp.left), titleCase(gp.right), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 3 & 5. Morton + 2nd-5th metatarsal extension share a card shape.
  const writeExtension = (
    label: string,
    mod: typeof ff.morton_extension | undefined,
  ): void => {
    if (!mod) return;
    const lActive = sideActive(mod.left_type);
    const rActive = sideActive(mod.right_type);
    if (!lActive && !rActive) return;
    const cb = makeCard();
    cb.addPair(`${label} — type`,
      titleCase(mod.left_type), titleCase(mod.right_type), lActive, rActive);
    cb.addPair(`${label} — thickness`,
      str(mod.left_thickness), str(mod.right_thickness), lActive, rActive);
    cb.addPair(`${label} — softness/rigidity`,
      extSoftnessLabel(mod.left_softness), extSoftnessLabel(mod.right_softness), lActive, rActive);
    const card = cb.toCard();
    if (card) cards.push(card);
  };
  writeExtension('Morton extension', ff.morton_extension);

  // 4. 1st Ray / MTPJ cut-out (wizard renders this BEFORE 2nd-5th ext).
  //    Hidden for sandals.
  const cut = ff.first_ray_cut_out;
  if (!isSandal && cut) {
    const lActive = sideActive(cut.left);
    const rActive = sideActive(cut.right);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('1st Ray / MTPJ cut-out',
        CUT_OUT_LABEL[cut.left ?? ''] ?? titleCase(cut.left),
        CUT_OUT_LABEL[cut.right ?? ''] ?? titleCase(cut.right),
        lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 5. 2nd-5th metatarsal extension. Hidden for sandals.
  if (!isSandal) writeExtension('2nd-5th metatarsal extension', ff.metatarsal_extension);

  // Observations moved to a dedicated PODIATRIST OBSERVATIONS section
  // at the end of the workbook (collected by buildSections).

  return cards.length ? { title: 'FOREFOOT MODIFICATIONS', cards } : null;
}

function buildOffloadingsSection(
  off: PrescriptionDeepState['offloadings'] | undefined,
  isSandal: boolean,
): Section | null {
  if (!off) return null;
  const cards: Row[][] = [];

  // 1. Metatarsal cut-outs (Horseshoe offloadings). Hidden for sandals.
  const mco = off.metatarsal_cut_outs;
  if (!isSandal && mco) {
    const lActive = sideActive(mco.left_location);
    const rActive = sideActive(mco.right_location);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Metatarsal cut-outs — location',
        mcoLocationLabel(mco.left_location), mcoLocationLabel(mco.right_location), lActive, rActive);
      cb.addPair('Metatarsal cut-outs — softness',
        mcoSoftnessLabel(mco.left_softness), mcoSoftnessLabel(mco.right_softness), lActive, rActive);
      cb.addPair('Metatarsal cut-outs — thickness',
        str(mco.left_thickness), str(mco.right_thickness), lActive, rActive);
      cb.addPair('Metatarsal cut-outs — Poron fill',
        yesNo(mco.left_poron_fill), yesNo(mco.right_poron_fill), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 2. Kinetic wedge. Hidden for sandals.
  const kw = off.kinetic_wedge;
  if (!isSandal && kw) {
    // No 'none' enum here — a side is active once a softness is picked.
    const lActive = !!kw.left_softness || !!(kw.left_thickness ?? kw.left_height);
    const rActive = !!kw.right_softness || !!(kw.right_thickness ?? kw.right_height);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Kinetic wedge — softness',
        hardnessLabel(kw.left_softness), hardnessLabel(kw.right_softness), lActive, rActive);
      cb.addPair('Kinetic wedge — thickness',
        str(kw.left_thickness ?? kw.left_height),
        str(kw.right_thickness ?? kw.right_height),
        lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 3. Subcapital bar
  const sb = off.subcapital_bar;
  if (sb) {
    const lActive = sideActive(sb.left);
    const rActive = sideActive(sb.right);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Subcapital bar — material',
        titleCase(sb.left), titleCase(sb.right), lActive, rActive);
      cb.addPair('Subcapital bar — thickness',
        str(sb.left_thickness), str(sb.right_thickness), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 4. Drop met pad
  const dmp = off.drop_met_pad;
  if (dmp) {
    const lActive = sideActive(dmp.left_location);
    const rActive = sideActive(dmp.right_location);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Drop met pad — location',
        spanLocationLabel(dmp.left_location), spanLocationLabel(dmp.right_location), lActive, rActive);
      cb.addPair('Drop met pad — thickness',
        str(dmp.left_thickness), str(dmp.right_thickness), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 5. MTT bar
  const mtt = off.mtt_bar;
  if (mtt) {
    const lActive = sideActive(mtt.left_location);
    const rActive = sideActive(mtt.right_location);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('MTT bar — location',
        spanLocationLabel(mtt.left_location), spanLocationLabel(mtt.right_location), lActive, rActive);
      cb.addPair('MTT bar — thickness',
        str(mtt.left_thickness), str(mtt.right_thickness), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 6. Metatarsal head cut-outs (M1-M5 checkboxes per side)
  const mhco = off.metatarsal_head_cut_outs;
  if (mhco) {
    const pick = (s: typeof mhco.left): string => {
      if (!s) return '';
      return (['m1', 'm2', 'm3', 'm4', 'm5'] as const)
        .filter((m) => s[m])
        .map((m) => m.toUpperCase())
        .join(', ');
    };
    const leftLabel = pick(mhco.left);
    const rightLabel = pick(mhco.right);
    const lActive = leftLabel !== '';
    const rActive = rightLabel !== '';
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Metatarsal head cut-outs',
        leftLabel, rightLabel, lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 6b. Toe pulp offloading (M1-M5 checkboxes per side) — same shape as the
  //     metatarsal head cut-outs. Previously omitted, so this instruction never
  //     reached the lab; now exported.
  const tpo = off.toe_pulp_offloading_heads;
  if (tpo) {
    const pick = (s: typeof tpo.left): string => {
      if (!s) return '';
      return (['m1', 'm2', 'm3', 'm4', 'm5'] as const)
        .filter((m) => s[m])
        .map((m) => m.toUpperCase())
        .join(', ');
    };
    const leftLabel = pick(tpo.left);
    const rightLabel = pick(tpo.right);
    const lActive = leftLabel !== '';
    const rActive = rightLabel !== '';
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Toe pulp offloading', leftLabel, rightLabel, lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // 7. Extra heel cushion pad
  const ehcp = off.extra_heel_cushion_pad;
  if (ehcp) {
    const lActive = sideActive(ehcp.left);
    const rActive = sideActive(ehcp.right);
    if (lActive || rActive) {
      const cb = makeCard();
      cb.addPair('Extra heel cushion pad — material',
        titleCase(ehcp.left), titleCase(ehcp.right), lActive, rActive);
      cb.addPair('Extra heel cushion pad — thickness',
        str(ehcp.left_thickness), str(ehcp.right_thickness), lActive, rActive);
      const card = cb.toCard();
      if (card) cards.push(card);
    }
  }

  // Observations moved to a dedicated PODIATRIST OBSERVATIONS section
  // at the end of the workbook (collected by buildSections).

  return cards.length ? { title: 'OFFLOADINGS', cards } : null;
}

function buildSandalSection(sandal: PrescriptionDeepState['sandal'] | undefined): Section | null {
  if (!sandal) return null;
  const rows: Row[] = [];
  // R36 configurator fields.
  push(rows, 'Model', titleCase(sandal.model));
  push(rows, 'Base color', titleCase(sandal.base_color?.replace(/_/g, ' ')));
  push(rows, 'Upper strap color', titleCase(sandal.upper_strap_color?.replace(/_/g, ' ')));
  push(rows, 'Top cover', titleCase(sandal.top_cover));
  push(rows, 'White midsole', titleCase(sandal.white_midsole));
  push(rows, 'Posterior strap', titleCase(sandal.posterior_strap));
  push(
    rows,
    'Upper strap size',
    sandal.upper_strap_size === 'large_cavus'
      ? 'Large (cavus foot)'
      : sandal.upper_strap_size === 'standard'
        ? 'Standard'
        : '',
  );
  push(rows, 'Custom name', str(sandal.custom_name));
  push(rows, 'Design remarks', str(sandal.design_remarks));
  // Legacy fields — kept so historic orders still export with their original data.
  push(rows, 'Upper style (legacy)', titleCase(sandal.upper_style));
  push(rows, 'Color (legacy)', titleCase(sandal.color));
  push(rows, 'Size (EU)', sandal.size_eu != null ? sandal.size_eu : '');
  push(rows, 'Custom engraving (legacy)', str(sandal.custom_engraving));
  return rows.length ? { title: 'SANDAL STYLE', rows } : null;
}

/** Podiatrist observations — collects free-text notes into a single section
 *  rendered at the END of the workbook: the line-level "Notes for the lab"
 *  (top-level `configuration.observations`, written by the prescription form and
 *  carried by migrated orders) first, then each modification group's own note
 *  (rearfoot, midfoot, forefoot, offloadings). Each non-empty note becomes one
 *  row; the section is omitted when nothing is present. NOTE: this line-level
 *  note is distinct from `order.notes` (a separate wizard textarea shown in the
 *  ORDER section) and from a patient note — it must reach the lab. */
function buildObservationsSection(
  deep: PrescriptionDeepState | undefined,
  lineNumber: number,
  labNote: unknown,
): Section | null {
  const rows: Row[] = [];
  push(rows, `Line ${lineNumber} — Notes for the lab`, str(labNote));
  if (deep) {
    push(rows, `Line ${lineNumber} — Rearfoot`, str(deep.rearfoot?.observations));
    push(rows, `Line ${lineNumber} — Midfoot`, str(deep.midfoot?.observations));
    push(rows, `Line ${lineNumber} — Forefoot`, str(deep.forefoot?.observations));
    push(rows, `Line ${lineNumber} — Offloadings`, str(deep.offloadings?.observations));
  }
  return rows.length ? { title: 'PODIATRIST OBSERVATIONS', rows } : null;
}

/** Marketplace fields (FITO, Ready-Made, Orthopaedic, OTS) — flat
 *  product info stored on the line configuration. */
function buildMarketplaceSection(cfg: Record<string, unknown>): Section | null {
  const rows: Row[] = [];
  push(rows, 'Catalog code', cfg.product_code != null ? String(cfg.product_code) : '');
  push(rows, 'Catalog product name', cfg.product_name != null ? String(cfg.product_name) : '');
  push(rows, 'Catalog family', cfg.family != null ? String(cfg.family) : '');
  push(rows, 'Format', cfg.format != null ? String(cfg.format) : '');
  push(rows, 'Size (EU)', cfg.size_eu != null ? String(cfg.size_eu) : '');
  if (cfg.size) push(rows, 'Size detail', JSON.stringify(cfg.size));
  return rows.length ? { title: 'CATALOG ITEM', rows } : null;
}

/* ---------- Section assembly ----------------------------------------- */

/** Build every section for an order in the canonical wizard order:
 *   ORDER · PATIENT · (per line: LINE · INSOLE DESIGN · REARFOOT ·
 *   MIDFOOT · FOREFOOT · OFFLOADINGS · SANDAL · CATALOG ITEM)
 */
function buildSections(order: OrderDetail): Section[] {
  const sections: Section[] = [];
  sections.push(buildOrderSection(order));
  sections.push(buildPatientSection(order));

  // Collected and emitted as a single final section after all lines so
  // observations sit at the very end of the workbook regardless of how
  // many lines the order has.
  const observationRows: Row[] = [];

  for (const line of order.lines) {
    sections.push(buildLineSection(line));

    const cfg = (line.configuration ?? {}) as Record<string, unknown>;
    const deep = cfg.deep as PrescriptionDeepState | undefined;
    // Sandal lines run a different form: INSOLE DESIGN is replaced by SANDAL
    // STYLE, and the shared modification sections hide the insole-only cards.
    // The Excel is the manufacturing spec, so it mirrors what the sandal form
    // actually captures (matches the form's own `isSandal` derivation).
    const isSandal =
      line.solution_subtype === 'orthopaedic_sandal' ||
      line.line_type === 'orthopedic_sandal';
    // Refurbishment re-covers an EXISTING shell, so the form deliberately
    // renders no modification sections at all (PrescriptionForm's
    // `!isRefurbishment` gate). The Excel is the manufacturing spec and must
    // mirror the CURRENT form rules, so it must not print modification cards
    // the operator was never shown and cannot correct - otherwise the lab is
    // told to mill, for example, a -3mm carbosan heel recess into a shell that
    // is only being re-covered. This gate is deliberately here rather than only
    // at write time, so a line whose stored configuration already carries stale
    // modification state cannot produce a wrong sheet either.
    // NOTE the asymmetry: the DESIGN section still renders for refurbishment
    // (buildInsoleDesignSection has its own isRefurb handling and prints
    // "Refurbishment remarks"), which mirrors the form. Only the MODIFICATION
    // sections are suppressed.
    const isRefurbLine = line.solution_subtype === 'insoles_refurbishment';
    if (deep) {
      if (!isSandal) {
        const insole = buildInsoleDesignSection(deep.design, line.solution_subtype, order.order_number);
        if (insole) sections.push(insole);
      }
      if (!isRefurbLine) {
        const rf = buildRearfootSection(deep.rearfoot, isSandal);
        if (rf) sections.push(rf);
        const mf = buildMidfootSection(deep.midfoot, isSandal);
        if (mf) sections.push(mf);
        const ff = buildForefootSection(deep.forefoot, isSandal);
        if (ff) sections.push(ff);
        const off = buildOffloadingsSection(deep.offloadings, isSandal);
        if (off) sections.push(off);
        const sd = buildSandalSection(deep.sandal);
        if (sd) sections.push(sd);
      }
    }

    // Stash this line's observations (line-level "Notes for the lab" + each
    // modification group's note) for the dedicated final section. Done outside
    // the `deep` guard so a note survives even on a line without a deep config.
    const obsSection = buildObservationsSection(deep, line.line_number, cfg.observations);
    if (obsSection?.rows) observationRows.push(...obsSection.rows);

    // Marketplace fields are only present on non-prescription lines.
    if (cfg.product_code || cfg.product_name || cfg.family) {
      const mk = buildMarketplaceSection(cfg);
      if (mk) sections.push(mk);
    }
  }

  // Final PODIATRIST OBSERVATIONS section — only when at least one
  // group had a non-empty observation across any line.
  if (observationRows.length > 0) {
    sections.push({ title: 'PODIATRIST OBSERVATIONS', rows: observationRows });
  }

  // Drop sections with zero rows so the empty-row separator logic
  // doesn't produce a run of blank rows. A section qualifies if it has
  // non-empty `rows` OR at least one non-empty `cards` group.
  return sections.filter((s) => {
    if (s.rows && s.rows.length > 0) return true;
    if (s.cards && s.cards.some((c) => c.length > 0)) return true;
    return false;
  });
}

/* ---------- Workbook writer ------------------------------------------ */

/** Cell-style theme. Kept inside one object so a future palette tweak
 *  is a one-place change. Hex colours are without the leading '#' per
 *  the xlsx-js-style API. */
const STYLE = {
  header: {
    fill: { patternType: 'solid', fgColor: { rgb: 'F5F5F5' } },
    font: { bold: true },
  } as const,
  sectionHeader: {
    fill: { patternType: 'solid', fgColor: { rgb: 'E8EAED' } },
    font: { bold: true },
  } as const,
  leftRow: {
    fill: { patternType: 'solid', fgColor: { rgb: 'E8F4F8' } },
  } as const,
  rightRow: {
    fill: { patternType: 'solid', fgColor: { rgb: 'FFEEE5' } },
  } as const,
  /** Multi-pair quantity rows: amber fill + bold dark-orange text, one step
   *  louder than the pastel L/R fills so it reads first on a printed sheet. */
  emphasisRow: {
    fill: { patternType: 'solid', fgColor: { rgb: 'FFEDD5' } },
    font: { bold: true, sz: 13, color: { rgb: '9A3412' } },
  } as const,
  /** Patient notes — 8pt so a long free-text block stays compact. */
  smallFontRow: {
    font: { sz: 8 },
  } as const,
  /** Engraving note under the header — small, italic, muted grey. */
  note: {
    font: { sz: 9, italic: true, color: { rgb: '6B7280' } },
  } as const,
};

/** Cell ref helper — 0-based row & col → "A1" / "B2" / etc. */
function cellRef(row: number, col: number): string {
  let s = '';
  let c = col;
  do {
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26) - 1;
  } while (c >= 0);
  return `${s}${row + 1}`;
}

/**
 * Build the styled two-column prescription workbook. Shared by the download
 * (exportOrderToExcel) and the email-attachment byte path
 * (getPrescriptionExcelBytes). Switch from 'xlsx' to 'xlsx-js-style' is local
 * to this file (and apps/web/package.json); the rest of the app still imports
 * 'xlsx'.
 */
export async function buildPrescriptionWorkbook(order: OrderDetail) {
  // Dynamic import — bundle splitting keeps the main app slim.
  const XLSX = await import('xlsx-js-style');

  const sections = buildSections(order);

  // Build the AoA + ONE parallel `rowMeta` array (side fill + emphasis per
  // row) so cell styles can be applied after the sheet is created. A single
  // metadata array keeps the per-row style data from desyncing when a new
  // push site is added. Index 0 is the header row.
  type RowMeta = { side: Side; emphasis: boolean; smallFont: boolean };
  const PLAIN: RowMeta = { side: null, emphasis: false, smallFont: false };
  const aoa: (string | number)[][] = [['Field', 'Value']];
  const rowMeta: RowMeta[] = [PLAIN];
  const sectionHeaderRows: number[] = [];

  // One-line engraving note directly under the header so the supplier
  // understands why every PA12 / TPU engraving starts with a number.
  const ENGRAVING_NOTE =
    'Note: the order ID is lasered onto every PA12 / TPU engraving for order management and traceability, so each engraving starts with the order number.';
  aoa.push([ENGRAVING_NOTE, '']);
  rowMeta.push(PLAIN);
  const noteRowIdx = aoa.length - 1;

  const pushRow = (cells: [string | number, string | number], meta: RowMeta): void => {
    aoa.push(cells);
    rowMeta.push(meta);
  };

  sections.forEach((section, sectionIdx) => {
    // Empty separator row BETWEEN sections (not before the first one).
    if (sectionIdx > 0) {
      pushRow(['', ''], PLAIN);
    }
    // Section header row.
    sectionHeaderRows.push(aoa.length);
    pushRow([section.title, ''], PLAIN);
    // Section body — flat rows or card groups (one blank row between
    // cards, never trailing).
    if (section.rows) {
      for (const r of section.rows) {
        pushRow([r.label, r.value], { side: r.side, emphasis: r.emphasis === true, smallFont: r.smallFont === true });
      }
    } else if (section.cards) {
      const nonEmptyCards = section.cards.filter((c) => c.length > 0);
      nonEmptyCards.forEach((card, cardIdx) => {
        if (cardIdx > 0) {
          pushRow(['', ''], PLAIN);
        }
        for (const r of card) {
          pushRow([r.label, r.value], { side: r.side, emphasis: r.emphasis === true, smallFont: r.smallFont === true });
        }
      });
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Span the engraving note across both columns so it reads as a banner.
  ws['!merges'] = [
    ...(ws['!merges'] ?? []),
    { s: { r: noteRowIdx, c: 0 }, e: { r: noteRowIdx, c: 1 } },
  ];

  // Column widths — label column kept at a reasonable size, value
  // column capped at 60 so very long values (e.g. podiatrist
  // observations) wrap inside the cell instead of stretching the sheet.
  ws['!cols'] = [{ wch: 50 }, { wch: 60 }];

  // Apply styles cell-by-cell. xlsx-js-style attaches styles via the
  // `s` key on each cell object.
  const setStyle = (rowIdx: number, colIdx: number, style: Record<string, unknown>): void => {
    const addr = cellRef(rowIdx, colIdx);
    const cell = (ws as Record<string, unknown>)[addr] as { s?: Record<string, unknown> } | undefined;
    if (cell) {
      // Merge so existing value/type props are preserved AND nested
      // alignment object isn't lost when a later setStyle adds a fill.
      const prev = (cell.s ?? {}) as Record<string, unknown>;
      const prevAlign = (prev.alignment ?? {}) as Record<string, unknown>;
      const nextAlign = (style.alignment ?? {}) as Record<string, unknown>;
      cell.s = {
        ...prev,
        ...style,
        alignment: { ...prevAlign, ...nextAlign },
      };
    }
  };

  // Global alignment applied to EVERY cell first — left/top with
  // wrapText so long values (especially podiatrist observations) wrap
  // inside the capped column width instead of overflowing.
  const baseAlignment = {
    alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
  };
  for (let r = 0; r < aoa.length; r++) {
    setStyle(r, 0, baseAlignment);
    setStyle(r, 1, baseAlignment);
  }

  // Header row (Field / Value labels)
  // Engraving note row (muted italic), just under the header.
  setStyle(noteRowIdx, 0, STYLE.note);
  setStyle(noteRowIdx, 1, STYLE.note);

  setStyle(0, 0, STYLE.header);
  setStyle(0, 1, STYLE.header);

  // Section title rows — light gray fill spans both columns.
  for (const rowIdx of sectionHeaderRows) {
    setStyle(rowIdx, 0, STYLE.sectionHeader);
    setStyle(rowIdx, 1, STYLE.sectionHeader);
  }

  // L/R fills, then emphasis (multi-quantity) — emphasis applied last so
  // it wins over any fill applied above.
  rowMeta.forEach((meta, rowIdx) => {
    if (meta.side === 'L') {
      setStyle(rowIdx, 0, STYLE.leftRow);
      setStyle(rowIdx, 1, STYLE.leftRow);
    } else if (meta.side === 'R') {
      setStyle(rowIdx, 0, STYLE.rightRow);
      setStyle(rowIdx, 1, STYLE.rightRow);
    }
    if (meta.emphasis) {
      setStyle(rowIdx, 0, STYLE.emphasisRow);
      setStyle(rowIdx, 1, STYLE.emphasisRow);
    }
    if (meta.smallFont) {
      setStyle(rowIdx, 0, STYLE.smallFontRow);
      setStyle(rowIdx, 1, STYLE.smallFontRow);
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Prescription');
  return { XLSX, wb };
}

/** Suggested filename for the prescription .xlsx. */
export function prescriptionExcelFilename(order: OrderDetail): string {
  return `INSOLECH-${order.order_number}-prescription.xlsx`;
}

/** Download the order's full prescription as a styled two-column .xlsx. */
export async function exportOrderToExcel(order: OrderDetail): Promise<void> {
  const { XLSX, wb } = await buildPrescriptionWorkbook(order);
  XLSX.writeFile(wb, prescriptionExcelFilename(order));
}

/**
 * The same prescription workbook as the download, returned as .xlsx bytes so it
 * can be attached to an email (Phase 3 supplier draft). No download side effect.
 */
export async function getPrescriptionExcelBytes(order: OrderDetail): Promise<Uint8Array> {
  const { XLSX, wb } = await buildPrescriptionWorkbook(order);
  const out: unknown = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return out instanceof Uint8Array ? out : new Uint8Array(out as ArrayBuffer);
}

/* ---------- Back-compat export ---------------------------------------- */

/**
 * Legacy flat row shape — kept so any external caller (tests / other
 * exporters) that depended on the R35 `flattenOrderPrescription` API
 * keeps working. New code should consume `buildSections` instead.
 */
export type PrescriptionRow = readonly [string, string | number | null];

export function flattenOrderPrescription(order: OrderDetail): PrescriptionRow[] {
  const out: PrescriptionRow[] = [];
  const sections = buildSections(order);
  sections.forEach((section, idx) => {
    if (idx > 0) out.push(['', '']);
    out.push([`--- ${section.title} ---`, '']);
    if (section.rows) {
      for (const r of section.rows) {
        out.push([r.label, r.value === '' ? null : r.value]);
      }
    } else if (section.cards) {
      const nonEmptyCards = section.cards.filter((c) => c.length > 0);
      nonEmptyCards.forEach((card, cardIdx) => {
        if (cardIdx > 0) out.push(['', '']);
        for (const r of card) {
          out.push([r.label, r.value === '' ? null : r.value]);
        }
      });
    }
  });
  return out;
}
